import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/currency-input'
import { useToast } from '@/hooks/use-toast'
import { Loader2, ArrowRight, ArrowLeftRight } from 'lucide-react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'

type FlatRateZone = Tables<'flat_rate_zones'>
type FlatRateOverride = Tables<'flat_rate_overrides'>
type Vehicle = Tables<'vehicles'>

interface FlatRateOverrideWithZones extends FlatRateOverride {
  pickup_zone: FlatRateZone
  dropoff_zone: FlatRateZone
}

const overrideSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  pickup_zone_id: z.string().uuid('Select a pickup zone'),
  dropoff_zone_id: z.string().uuid('Select a dropoff zone'),
  direction: z.enum(['directional', 'bidirectional']),
  flat_rate: z.coerce.number().min(1, 'Flat rate must be at least $1'),
  apply_to_all_vehicles: z.boolean(),
  vehicle_ids: z.array(z.string()).optional(),
  priority: z.coerce.number().int().min(0),
  is_active: z.boolean().default(true),
})

type OverrideFormData = z.infer<typeof overrideSchema>

interface OverrideDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  override: FlatRateOverrideWithZones | null
  zones: FlatRateZone[]
  vehicles: Vehicle[]
  accountId: string
}

export function OverrideDialog({
  open,
  onOpenChange,
  override,
  zones,
  vehicles,
  accountId,
}: OverrideDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!override

  const form = useForm<OverrideFormData>({
    resolver: zodResolver(overrideSchema),
    defaultValues: {
      name: '',
      description: '',
      pickup_zone_id: '',
      dropoff_zone_id: '',
      direction: 'bidirectional',
      flat_rate: 0,
      apply_to_all_vehicles: true,
      vehicle_ids: [],
      priority: 0,
      is_active: true,
    },
  })

  const applyToAllVehicles = form.watch('apply_to_all_vehicles')
  const pickupZoneId = form.watch('pickup_zone_id')
  const dropoffZoneId = form.watch('dropoff_zone_id')
  const direction = form.watch('direction')

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (override) {
        form.reset({
          name: override.name,
          description: override.description || '',
          pickup_zone_id: override.pickup_zone_id,
          dropoff_zone_id: override.dropoff_zone_id,
          direction: override.direction as 'directional' | 'bidirectional',
          flat_rate: override.flat_rate,
          apply_to_all_vehicles: !override.vehicle_ids,
          vehicle_ids: override.vehicle_ids || [],
          priority: override.priority,
          is_active: override.is_active,
        })
      } else {
        form.reset({
          name: '',
          description: '',
          pickup_zone_id: zones[0]?.id || '',
          dropoff_zone_id: zones[1]?.id || zones[0]?.id || '',
          direction: 'bidirectional',
          flat_rate: 0,
          apply_to_all_vehicles: true,
          vehicle_ids: [],
          priority: 0,
          is_active: true,
        })
      }
    }
  }, [open, override, zones, form])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: OverrideFormData) => {
      const { error } = await supabase.from('flat_rate_overrides').insert({
        account_id: accountId,
        name: data.name,
        description: data.description || null,
        pickup_zone_id: data.pickup_zone_id,
        dropoff_zone_id: data.dropoff_zone_id,
        direction: data.direction,
        flat_rate: data.flat_rate,
        vehicle_ids: data.apply_to_all_vehicles ? null : data.vehicle_ids,
        priority: data.priority,
        is_active: data.is_active,
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-overrides', accountId] })
      toast({
        title: 'Flat rate created',
        description: 'The flat rate override has been created successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: OverrideFormData) => {
      if (!override) throw new Error('No override to update')

      const { error } = await supabase
        .from('flat_rate_overrides')
        .update({
          name: data.name,
          description: data.description || null,
          pickup_zone_id: data.pickup_zone_id,
          dropoff_zone_id: data.dropoff_zone_id,
          direction: data.direction,
          flat_rate: data.flat_rate,
          vehicle_ids: data.apply_to_all_vehicles ? null : data.vehicle_ids,
          priority: data.priority,
          is_active: data.is_active,
        })
        .eq('id', override.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-overrides', accountId] })
      toast({
        title: 'Flat rate updated',
        description: 'The flat rate override has been updated successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: OverrideFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  // Get zone by ID
  const getZone = (id: string) => zones.find((z) => z.id === id)
  const pickupZone = getZone(pickupZoneId)
  const dropoffZone = getZone(dropoffZoneId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Flat Rate' : 'Create Flat Rate Override'}
          </DialogTitle>
          <DialogDescription>
            Define a fixed price for trips between two zones. When a booking matches,
            this flat rate will replace distance-based pricing.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Airport Transfer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe when this flat rate applies..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Zone Selection */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="pickup_zone_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pickup Zone</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select zone" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {zones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: zone.color }}
                              />
                              {zone.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dropoff_zone_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dropoff Zone</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select zone" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {zones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: zone.color }}
                              />
                              {zone.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Route Preview */}
            {pickupZone && dropoffZone && (
              <div className="flex items-center justify-center gap-2 py-2 px-4 bg-muted/50 rounded-lg text-sm">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: pickupZone.color }}
                />
                <span className="font-medium">{pickupZone.name}</span>
                {direction === 'bidirectional' ? (
                  <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                )}
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: dropoffZone.color }}
                />
                <span className="font-medium">{dropoffZone.name}</span>
              </div>
            )}

            {/* Direction */}
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Direction</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="bidirectional" id="bidirectional" />
                        <Label
                          htmlFor="bidirectional"
                          className="flex items-center gap-1.5 cursor-pointer"
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                          Both Directions
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="directional" id="directional" />
                        <Label
                          htmlFor="directional"
                          className="flex items-center gap-1.5 cursor-pointer"
                        >
                          <ArrowRight className="h-4 w-4" />
                          One Direction Only
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    {field.value === 'bidirectional'
                      ? 'Applies to trips in either direction between the zones'
                      : 'Only applies when pickup is in the first zone and dropoff is in the second'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Flat Rate Price */}
            <FormField
              control={form.control}
              name="flat_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Flat Rate Price</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      className="w-40"
                    />
                  </FormControl>
                  <FormDescription>
                    This fixed price replaces the distance-based calculation
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vehicle Selection */}
            <FormField
              control={form.control}
              name="apply_to_all_vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Apply to All Vehicles</FormLabel>
                    <FormDescription>
                      Or select specific vehicles this rate applies to
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!applyToAllVehicles && (
              <FormField
                control={form.control}
                name="vehicle_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Vehicles</FormLabel>
                    <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto space-y-2">
                      {vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <div key={vehicle.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={vehicle.id}
                              checked={field.value?.includes(vehicle.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), vehicle.id]
                                  : (field.value || []).filter((id) => id !== vehicle.id)
                                field.onChange(newValue)
                              }}
                            />
                            <label
                              htmlFor={vehicle.id}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {vehicle.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No vehicles available
                        </p>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Priority */}
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Lower number = higher priority. Used when multiple overrides match.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Active Toggle */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive overrides won't be applied to bookings
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Flat Rate'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
